import React, {useState} from 'react';
import {View, Text, Image} from 'react-native';
import {SvgUri} from 'react-native-svg';
import Ionicons from 'react-native-vector-icons/Ionicons';

import StarAsset from '@/Assets/Recognition/Star.svg';
import FlashAsset from '@/Assets/Recognition/Flash.svg';
import StreakAsset from '@/Assets/Recognition/Streak.svg';
import CrownAsset from '@/Assets/Recognition/Crown.svg';
import TrophyAsset from '@/Assets/Recognition/Trophy.svg';
import GreatEffortAsset from '@/Assets/Recognition/GreatEffort.png';
import GoodThinkingAsset from '@/Assets/Recognition/GoodThinking.png';
import OnTimeAsset from '@/Assets/Recognition/OnTime.png';

// keys that render as vector icons (white SVG originals replaced)
const ICON_OVERRIDES: Record<string, {name: string}> = {
  focused:      {name: 'eye'},
  correct_solve: {name: 'checkmark-circle'},
};

const LOCAL_ASSETS: Record<string, any> = {
  good_try: StarAsset,
  great_effort: GreatEffortAsset,
  good_thinking: GoodThinkingAsset,
  team_player: TrophyAsset,
  joined_on_time: OnTimeAsset,
  homework_complete: TrophyAsset,
  fastest_finger: FlashAsset,
  puzzle_attempt: StarAsset,
  mystery_bonus: CrownAsset,
};

const EMOJI_FALLBACK: Record<string, string> = {
  good_try: '🌱',
  great_effort: '💪',
  good_thinking: '💡',
  focused: '🎯',
  team_player: '🤝',
  joined_on_time: '⏰',
  homework_complete: '📚',
  puzzle_attempt: '♟',
  correct_solve: '✓',
  fastest_finger: '⚡',
  mystery_bonus: '🎁',
};

interface AwardIconProps {
  awardKey: string;
  iconUrl?: string | null;
  size?: number;
  color?: string;
}

export function AwardIcon({awardKey, iconUrl, size = 18, color}: AwardIconProps) {
  const [hasError, setHasError] = useState(false);

  const iconOverride = ICON_OVERRIDES[awardKey];
  if (iconOverride) {
    return <Ionicons name={iconOverride.name} size={size} color={color ?? '#4D3F37'} />;
  }

  if (iconUrl && !hasError) {
    const isSvg = iconUrl.toLowerCase().endsWith('.svg') || iconUrl.includes('image/svg');
    if (isSvg) {
      return (
        <View testID="award-icon-svg" style={{width: size, height: size}}>
          <SvgUri uri={iconUrl} width={size} height={size} onError={() => setHasError(true)} />
        </View>
      );
    }
    return (
      <Image
        testID="award-icon-img"
        source={{uri: iconUrl}}
        style={{width: size, height: size, resizeMode: 'contain'}}
        onError={() => setHasError(true)}
      />
    );
  }

  const localAsset = LOCAL_ASSETS[awardKey];
  if (localAsset) {
    return (
      <Image
        testID="award-icon-local"
        source={localAsset}
        style={{width: size, height: size, resizeMode: 'contain'}}
      />
    );
  }

  return <Text style={{fontSize: size, color}}>{EMOJI_FALLBACK[awardKey] ?? '⭐'}</Text>;
}
